"use client";

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import TinderCard from "react-tinder-card";
import type { ExchangeCard } from "@/lib/flatten-dialog";
import type { TTSState } from "@/lib/use-tts";
import { CueCard } from "./cue-card";

interface CardCarouselProps {
  cards: ExchangeCard[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  userDetails: Record<string, string>;
  onSpeak: (text: string) => void;
  onStopSpeak: () => void;
  ttsState: TTSState;
}

type TinderCardRef = { swipe: (dir?: string) => Promise<void>; restoreCard: () => Promise<void> };

const STACK_SIZE = 3;

export function CardCarousel({
  cards,
  currentIndex,
  onIndexChange,
  userDetails,
  onSpeak,
  onStopSpeak,
  ttsState,
}: CardCarouselProps) {
  const topCardRef = useRef<TinderCardRef>(null);
  const [swiping, setSwiping] = useState(false);
  const swipeDir = useRef<string>("left");

  const next = useCallback(() => {
    onIndexChange((currentIndex + 1) % cards.length);
  }, [currentIndex, cards.length, onIndexChange]);

  const prev = useCallback(() => {
    onIndexChange((currentIndex - 1 + cards.length) % cards.length);
  }, [currentIndex, cards.length, onIndexChange]);

  // Swipe started — remember direction, block further input
  const handleSwipe = useCallback((direction: string) => {
    swipeDir.current = direction;
    setSwiping(true);
  }, []);

  // Card fully left screen — drag left = forward, drag right = back
  const handleCardLeftScreen = useCallback(() => {
    setSwiping(false);
    if (swipeDir.current === "left") next();
    else prev();
  }, [next, prev]);

  // Keyboard: ArrowLeft = forward, ArrowRight = back
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (swiping) return;
      if (e.key === "ArrowLeft") {
        topCardRef.current?.swipe("left");
      } else if (e.key === "ArrowRight") {
        topCardRef.current?.swipe("right");
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [swiping]);

  // Build the visible window wrapping around for infinite stack
  const visibleSlice = useMemo(() => {
    const slice: { card: ExchangeCard; depth: number; idx: number }[] = [];
    for (let d = 0; d < Math.min(STACK_SIZE, cards.length); d++) {
      const idx = (currentIndex + d) % cards.length;
      slice.push({ card: cards[idx]!, depth: d, idx });
    }
    return slice;
  }, [cards, currentIndex]);

  if (cards.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        No cards available
      </div>
    );
  }

  return (
    <div className="flex-1 relative" style={{ minHeight: 280 }}>
      {/* Render deepest first, top card last (natural DOM stacking) */}
      {[...visibleSlice].reverse().map(({ card, depth, idx }) => (
        <div
          key={card.id}
          className="absolute"
          style={{
            top: 12 + depth * 8,
            left: 12,
            right: 12,
            bottom: 12 - depth * 8,
            zIndex: STACK_SIZE - depth,
            transform: depth > 0 ? `scale(${1 - depth * 0.03})` : undefined,
            transition: "all 0.3s ease",
            pointerEvents: depth === 0 ? "auto" : "none",
          }}
        >
          {depth === 0 ? (
            <TinderCard
              ref={topCardRef as React.Ref<TinderCardRef>}
              onSwipe={handleSwipe}
              onCardLeftScreen={handleCardLeftScreen}
              preventSwipe={["up", "down"]}
              swipeRequirementType="position"
              swipeThreshold={40}
              className="h-full"
            >
              <CueCard
                card={card}
                userDetails={userDetails}
                index={idx}
                total={cards.length}
                onSpeak={onSpeak}
                onStopSpeak={onStopSpeak}
                ttsState={ttsState}
              />
            </TinderCard>
          ) : depth === 1 ? (
            <div className="h-full">
              <CueCard
                card={card}
                userDetails={userDetails}
                index={idx}
                total={cards.length}
                onSpeak={onSpeak}
                onStopSpeak={onStopSpeak}
                ttsState={ttsState}
              />
            </div>
          ) : (
            <div className="h-full opacity-60">
              <div className="h-full rounded-2xl bg-card shadow-md border border-border/30" />
            </div>
          )}
        </div>
      ))}

      {/* Forward (swipe left) / Back (swipe right) tap zones */}
      <button
        onClick={() => { if (!swiping) topCardRef.current?.swipe("left"); }}
        className="absolute left-0 top-0 bottom-0 w-12 z-0 opacity-0"
        aria-label="Next card"
      />
      <button
        onClick={() => { if (!swiping) topCardRef.current?.swipe("right"); }}
        className="absolute right-0 top-0 bottom-0 w-12 z-0 opacity-0"
        aria-label="Previous card"
      />
    </div>
  );
}
